/* ── BRIEF 125 §6 — ONE ROW RENDERER, TWO SURFACES ──────────────────────────
 *
 * Laurent, on the glance of #430: "the login reminder panel is useless, shows
 * only task descriptions without knowing the case: just let it show the exact
 * content of the bell menu."
 *
 * [MEASURED at 3bf7238f] Two renderers of the same material. The bell's rows
 * (App.tsx:13505+) carried a TYPE BADGE, the MATTER'S TITLE, its REF, the date,
 * a DISMISS action and, where the event has a join link, JOIN & TIME. The Daily
 * Overview's rows (App.tsx:16586+, the module-scope `SEC`) carried a sentence
 * and a date — `n.msg` and `fd(n.ts)`, nothing else. Same notifications, two
 * shapes, and one of them had been quietly losing the case for months.
 *
 * THIS IS B.3 IN A THIRD DRESS — the same cure as one modal and one participant
 * list. The row moved here verbatim (rule 3: extract, don't improve); the two
 * surfaces mount it and differ only in what they hand it.
 *
 * ── WHAT LEGITIMATELY DIFFERS, AND STAYS ───────────────────────────────────
 *
 * §6: "Grouping and framing may stay; the ROW may not fork." So the Daily
 * Overview keeps, in its own file:
 *   · the OVERDUE / DUE TODAY / REMINDERS grouping with its coloured headings
 *     and counts — it opens once per sign-in and leads with a triage;
 *   · `SignInLine variant="modal"`, "Don't show again today", "Dismiss all",
 *     "Close" — the modal's frame;
 * and the bell keeps its own: "mark all read", the day's quote, the 20-row cap.
 * None of those is the row.
 *
 * ── THE TWO ACTIONS, ENUMERATED PER ACTION AS §6 ASKS ──────────────────────
 *
 * DISMISS — KEPT in both. The overview already dismisses, in bulk, into the
 * same `dismissedReminders` plane (`App.tsx`'s "Dismiss all"); a per-row
 * Dismiss is that same act at one row, and having the bulk control without the
 * single one was the odd half.
 *
 * JOIN & TIME — KEPT in both. The overview's DUE TODAY group is precisely
 * today's meetings, and the morning modal is the surface where "join it now"
 * is most useful. It is offered on the same condition either way: the LIVE
 * event carries a `joinUrl`, asked of the row rather than of the notification,
 * so a link added after the alert fired still works and one removed since does
 * not.
 *
 * ── THE ONE PLACE THE SURFACES DISAGREED ABOUT A COLOUR, AND WHO WINS ──────
 *
 * `SEC` coloured the row's icon with its SECTION's colour (Overdue red, Due
 * Today amber, Reminders blue); the bell colours it from `n.type` (overdue red,
 * event blue, chat green, timer amber, else grey). The ROW'S OWN mapping wins,
 * because the row may not fork — the section heading keeps its colour, which is
 * the framing that may.
 */
import { Video } from "lucide-react";
import { fd } from "../utils";
import { resolveMatter } from "../lib/matterLabel"; // 191 C.2b
import { FS, RAD } from "../lib/tokens";
import type { NotifRow as NotifRowFace } from "../utils";
import type { TFn } from "../lib/taxonomyTypes";

/* ── THE SHAPES, WRITTEN OUT RATHER THAN REACHED FOR WITH `any` ────────────
 *
 * The block below moved out of App.tsx verbatim and its lambdas came with it,
 * typed `any` because App's own arrays are. The ratchet may only shrink, and
 * SESSION_RULES is explicit that a MOVE is the only thing re-recording a
 * baseline is for: new code that adds an `any` gets a type. So these are the
 * fields this row actually reads, and nothing else — structural, so App's much
 * richer objects satisfy them without being described here. */
/* 191 C.2b — the resolver's own row shape (a real matter row has an id and a
   string title); the local narrowing used to allow null on both. */
type Matter = import("../lib/matterLabel").MatterRow;
type Parent = {
  id?: number | null;
  type?: string | null;
  caId?: number | null;
  caseId?: number | null;
  ccId?: number | null;
  joinUrl?: string | null;
  subtasks?: { id?: number | null }[] | null;
};
type EvtEntry = { code?: string | null; meta?: { color?: string | null } | null };
/** What the alert payload states about a PERSISTED row (116 §4). */
type NotifCtx = {
  kind?: string | null;
  entityId?: number | null;
  typeCode?: string | null;
  caseId?: number | null;
  ccId?: number | null;
};
/** 126 §2 — WHAT MAKES A NON-DISMISSIBLE ROW GO AWAY.
 *
 * Five answers, and the row states which one is its own — it is NOT derived
 * here from `type`, and that is a measurement rather than a preference:
 * `ovd-` and `book-sec-` are BOTH `type: "overdue"` and are cured by different
 * acts (do the task / bring the book back). A renderer deciding from `type`
 * would have to be wrong about one of them. Same rule as `_item` and `_ctx`:
 * a fact the row already knows where it is built is not re-derived at a
 * second site. */
type ClearsBy = "task" | "event" | "read" | "timer" | "book";
/** The bell row as the aggregator builds it — the fields this renderer reads. */
type NotifItem = NotifRowFace & {
  type?: string | null;
  icon?: React.ReactNode;
  msg?: React.ReactNode;
  ref?: string | null;
  ts?: string | null;
  nav?: (() => void) | null;
  _ctx?: NotifCtx | null;
  _clears?: ClearsBy | null;
};

/* ── 126 §2 — THE FIVE SENTENCES, IN ONE PLACE, AS LITERAL KEYS ─────────────
 *
 * A `t("notif.clears." + n._clears)` would be one line shorter and would be a
 * COMPUTED KEY — the shape `i18n-total`'s census cannot resolve and brief 36
 * spent a slice on. Written out, so every key this app can ask for is a string
 * a grep finds. */
function clearsText(t: TFn, k: ClearsBy): string {
  if (k === "task")
    return t("notif.clears.task", { fallback: "disappears when you do the task" });
  if (k === "event")
    return t("notif.clears.event", { fallback: "disappears when the day has passed" });
  if (k === "read")
    return t("notif.clears.read", { fallback: "disappears when you read the message" });
  if (k === "timer")
    return t("notif.clears.timer", { fallback: "disappears when you stop the timer" });
  return t("notif.clears.book", { fallback: "disappears when the book is back" });
}

type Props = {
  /** The notification row, as the aggregator built it. */
  n: NotifItem;
  /** Answered by the caller — `isNotifRead(n, notifRead)`, the ONE predicate
   *  the unread count also asks (brief 42 follow-on). */
  isRead: boolean;
  tasks: Parent[];
  agendaEvents: Parent[];
  cases: Matter[];
  collectiveCases: Matter[];
  evtEntries: EvtEntry[];
  /* The `NotifRow` FACE from utils.ts — only the fields read and dismiss
     touch. Structural, so App's richer row satisfies it without this file
     describing one. `unknown` would not type: App's handlers take that face,
     and a parameter is contravariant. */
  markNotifRead: (n: NotifRowFace) => void;
  dismissNotif: (n: NotifRowFace) => void;
  joinAndTime: (evId: number, url: string) => void;
  /** Close the surface this row is mounted in — the bell menu, or the Daily
   *  Overview modal. The only thing the two callers answer differently. */
  onActed: () => void;
  t: TFn;
};

export default function NotifRowView({
  n,
  isRead,
  tasks,
  agendaEvents,
  cases,
  collectiveCases,
  evtEntries,
  markNotifRead,
  dismissNotif,
  joinAndTime,
  onActed,
  t,
}: Props) {
  /* 116 §4 — THE EVENT THIS ROW IS ABOUT, resolved
     the two ways the bell already resolves parents
     (the block below does the same): a PERSISTED
     row states it (`_ctx`, from the alert
     payload), a DERIVED one spells it in its id
     prefix — `ev-<id>` / `agr-<id>`. Nothing new is
     parsed and nothing is threaded down. */
  const _evId =
    n._ctx && n._ctx.kind === "event"
      ? n._ctx.entityId
      : (() => {
          const _mm = /^(ev|agr)-(\d+)$/.exec(String(n.id || ""));
          return _mm ? +_mm[2] : null;
        })();
  /* The action is offered only when the event
     really carries a meeting link — the JoinBtn
     family's own condition, asked of the live row
     rather than of the notification, because a link
     added after the alert fired should still work
     and one removed since should not. */
  const _joinEv =
    _evId != null
      ? agendaEvents.find(
          (e: Parent) => !!e && e.id === +_evId,
        )
      : null;
  const _joinUrl = _joinEv && _joinEv.joinUrl;
  return (
    /* `key` moved to the two mount sites, where a list key belongs; nothing
       else in this block changed on the way out of App.tsx. */
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        padding: "12px 14px",
        marginBottom: 4,
        borderBottom:
          "1px solid rgba(255,255,255,.04)",
        cursor: "pointer",
        background: isRead
          ? "none"
          : "rgba(74,124,255,.04)",
        opacity: isRead ? 0.6 : 1,
      }}
      onClick={() => {
        /* Corr-4 (item 2a) — one notif type carries
           no nav; calling it threw and killed the
           click. Guard so every row stays clickable
           and lands the user on the item. */
        markNotifRead(n);
        if (typeof n.nav === "function") n.nav();
        onActed();
      }}
    >
      <span
        style={{
          color:
            n.type === "overdue"
              ? "var(--rd)"
              : n.type === "event"
                ? "var(--ac)"
                : n.type === "chat"
                  ? "var(--gn)"
                  : n.type === "timer"
                    ? "var(--am)"
                    : "var(--ts)",
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        {n.icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Corr-3 (item 4) — slightly larger for
            comfortable reading. */}
        <div
          style={{
            fontSize: FS.s14,
            color: "var(--tp)",
            lineHeight: 1.45,
          }}
        >
          {n.msg}
        </div>
        {(() => {
          /* M+7 Stage 3.3 — enrich bell rows with
             parent context (case title + type
             badge). Inline lookup; the aggregator
             stays untouched. For a DERIVED row the
             id prefix says which entity to find:
             task (ovd/tkr/del), agenda event
             (ev/agr), subtask (st — climb up to the
             parent task or event).
             BRIEF 42 FOLLOW-ON §2 — A PERSISTED ROW
             STATES ITS CONTEXT AND IS NOT PARSED.
             "db-" was never going to work here and
             adding it to the prefix list could not
             have fixed it: that id names a
             Notification row, not the task or event
             the alert is ABOUT, so there is nothing
             upstream for `_pid` to find. The row
             carries `_ctx` instead, built from the
             same payload the `ref` beside it comes
             from — which is how the bell comes to
             name the case the login modal already
             named.
             THE RENDERER BELOW IS UNTOUCHED. One
             block, fed two ways; not a second way
             of drawing a row. When `_ctx` is
             present `_pre` is forced empty so no
             prefix branch can match and the seeded
             values stand. */
          const _ctx = n._ctx;
          const _m =
            /^([a-z]+)-(\d+)$/.exec(String(n.id || ""));
          if (!_ctx && !_m) return null;
          const _pre = _ctx ? "" : _m ? _m[1] : "",
            _pid = _m ? +_m[2] : 0;
          let _kind = _ctx ? _ctx.kind : null,
            _typeCode = _ctx ? _ctx.typeCode || "" : "",
            _caseId = _ctx ? _ctx.caseId : null,
            _ccId = _ctx ? _ctx.ccId : null;
          if (
            _pre === "ovd" ||
            _pre === "tkr" ||
            _pre === "del"
          ) {
            const _tp = tasks.find(
              (x: Parent) => x.id === _pid,
            );
            if (_tp) {
              _kind = "task";
              _typeCode = _tp.type || "";
              _caseId = _tp.caseId;
              _ccId = _tp.ccId;
            }
          } else if (
            _pre === "ev" ||
            _pre === "agr"
          ) {
            const _ag = agendaEvents.find(
              (x: Parent) => x.id === _pid,
            );
            if (_ag) {
              _kind = "event";
              _typeCode = _ag.type || "";
              _caseId = _ag.caId ?? _ag.caseId;
              _ccId = _ag.ccId;
            }
          } else if (_pre === "st") {
            for (const _t of tasks) {
              if (
                Array.isArray(_t.subtasks) &&
                _t.subtasks.some(
                  (s: { id?: number | null }) => s.id === _pid,
                )
              ) {
                _kind = "task";
                _typeCode = _t.type || "";
                _caseId = _t.caseId;
                _ccId = _t.ccId;
                break;
              }
            }
            if (!_kind) {
              for (const _a of agendaEvents) {
                if (
                  Array.isArray(_a.subtasks) &&
                  _a.subtasks.some(
                    (s: { id?: number | null }) => s.id === _pid,
                  )
                ) {
                  _kind = "event";
                  _typeCode = _a.type || "";
                  _caseId = _a.caId ?? _a.caseId;
                  _ccId = _a.ccId;
                  break;
                }
              }
            }
          }
          /* 191 C.2b — its own expression (truthy ids; no matter has id 0),
             moved onto the one resolver. */
          const _cs = resolveMatter({ caseId: _caseId, ccId: _ccId }, cases, collectiveCases);
          const _title = _cs?.title || "";
          const _evMeta =
            _kind === "event" && _typeCode
              ? evtEntries.find(
                  (e: EvtEntry) => e.code === _typeCode,
                )
              : null;
          const _evColor =
            (_evMeta &&
              _evMeta.meta &&
              _evMeta.meta.color) ||
            "var(--ac)";
          if (!_typeCode && !_title) return null;
          return (
            <div
              style={{
                display: "flex",
                gap: 6,
                marginTop: 3,
                alignItems: "center",
                minWidth: 0,
              }}
            >
              {_typeCode && _kind === "task" && (
                <span
                  style={{
                    fontSize: FS.s9_5,
                    padding: "1px 5px",
                    borderRadius: RAD.r4,
                    background: "var(--as)",
                    color: "var(--tp)",
                    fontFamily:
                      "'JetBrains Mono',monospace",
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {_typeCode}
                </span>
              )}
              {_typeCode && _kind === "event" && (
                <span
                  style={{
                    fontSize: FS.s9_5,
                    padding: "1px 5px",
                    borderRadius: RAD.r4,
                    background: _evColor + "22",
                    color: _evColor,
                    border: `1px solid ${_evColor}55`,
                    fontFamily:
                      "'JetBrains Mono',monospace",
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {_typeCode}
                </span>
              )}
              {_title && (
                <span
                  title={_title}
                  style={{
                    fontSize: FS.s11,
                    color: "var(--ts)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  {_title}
                </span>
              )}
            </div>
          );
        })()}
        {/* ── 177 §2 — THE DEFICIT GOES TO THE SPAN THAT WAS BUILT TO TAKE IT ──
            Laurent: the meeting reminder card warps at the largest text size,
            and only that card. MEASURED at all four steps with
            `scripts/drive-177-reminder-row.js`, and the first thing the drive
            settles is that THE SIZE IS NOT THE VARIABLE. `textSize` does not
            grow `--base-font-size` — `apply()` pins it at 13px and scales the
            whole app with root CSS `zoom` instead (App.tsx:1615-1624,
            TEXT_SIZE_ZOOM :284-289). A uniform zoom cannot make a row that
            fits at `medium` overflow at `xlarge`, and it does not: this row
            wants MORE THAN IT HAS AT EVERY STEP — 14 / 13 / 6 / 11 px over, at
            small / medium / large / xlarge. Only the reader's eye scales.

            WHAT THE OVERFLOW DOES. The row cannot wrap and the button refuses
            to shrink (`flexShrink: 0`, :528), so flex takes the deficit out of
            the three items that can give: `n.ref`, the timestamp, and the
            italic "disappears when…" hint. The ref is the one that breaks —
            `min-width: auto` on a flex item is its MIN-CONTENT width, and a
            reference like "SMK-09" has a hyphen, which is a break opportunity.
            So the span is handed 37px for 40px of text and the reference wraps
            onto two lines: the row stands 28px instead of 18 and the card
            reads as broken. That is the warp, and it is present at medium too.

            THE FIX IS TO STOP ASKING THE WRONG ITEMS. The hint already carries
            `minWidth: 0` + `overflow: hidden` + `textOverflow: ellipsis`
            (:633-649) precisely so this slot can be narrow — it was built to
            absorb. `flexShrink: 0` here routes the whole deficit to it: the
            reference and the time keep their own width, the button keeps its
            label, and the hint gives up 3px of an ellipsis it was already
            drawing (and whose full text rides in its `title`). Measured after:
            the row is ONE line, 18-19px, at all four steps, with 109px of
            further give left in the absorber.

            WHAT THE OTHER TWO CANDIDATES DID, since §2 asks:
              · `flexWrap: "wrap"` on the row — the reference is whole, but the
                row splits into two flex lines and the CARD GROWS 10-11px AT
                EVERY SIZE (rowH 28 → 38-39). It changes `medium`, where
                Laurent works, more than the defect does.
              · `minWidth: 0` on the two spans — DOES NOT FIX IT. The ref is
                then handed 38px for the same 40px of text and still wraps
                (rowH stays 28); all it buys is 3px off the timestamp.
              · icons instead of button labels — fits with 113-120px to spare,
                but drops the word at every size to cure a defect that is not
                about size, and §2 is explicit that a truncated "Rejoindre" is
                worse than a wrapped row. A label removed is that, further on.
            `whiteSpace: "nowrap"` beside `flexShrink: 0` was measured as
            REDUNDANT — byte-identical geometry at all four steps — so it is
            not written: a flex item that cannot shrink already keeps its
            max-content width. */}
        <div
          style={{
            display: "flex",
            gap: 6,
            marginTop: 1,
            alignItems: "center",
          }}
        >
          {n.ref && (
            <span
              style={{
                fontSize: FS.s11,
                color: "var(--tm)",
                flexShrink: 0,
              }}
            >
              {n.ref}
            </span>
          )}
          <span
            style={{
              fontSize: FS.s11,
              color: "var(--tm)",
              flexShrink: 0,
            }}
          >
            {fd(n.ts)}
          </span>
          {/* 116 §4 — JOIN & TIME. The JoinBtn
              family's green outline, at this
              line's scale; the click opens the
              meeting and starts the event's timer,
              then marks the row read and closes the
              menu exactly as the row's own click
              does — a user who has joined has read
              it. `stopPropagation` so the row's
              navigation does not also fire: the
              point of the action is to stay put
              and let the §3 widget tick. */}
          {_joinUrl && (
            <button
              type="button"
              className="btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 7px",
                fontSize: FS.s9_5,
                fontWeight: 700,
                background: "rgba(74,222,128,.14)",
                color: "var(--gn)",
                border: "1px solid var(--gn)",
                borderRadius: RAD.r5,
                flexShrink: 0,
              }}
              onClick={(e7) => {
                e7.stopPropagation();
                joinAndTime(+_evId!, _joinUrl);
                markNotifRead(n);
                onActed();
              }}
            >
              <Video size={11} />
              {t("alerts.join_and_time", {
                fallback: "Join & time",
              })}
            </button>
          )}
          {n.dismissId && (
            <button
              /* ── 126 §6 — THE WORD SAYS WHAT IT DOES ────────────────────
                 [RULED] "'Dismiss' / 'Ignorer' reads as 'ignore', which is
                 not what it does. It DELETES the row from the list. Pick the
                 word that says that in all three languages." So the label is
                 `notif.remove` — Remove / Retirer / Verwijderen.
                 `notif.dismiss` keeps its seeded rows (D28) and keeps its
                 OTHER consumer: the Outlook-import popup's ✕ (App.tsx), which
                 MUTES the card for the session and is genuinely a dismissal.

                 AND THE TOOLTIP WAS PLANE-ACCURATE, because the act was not
                 one act. MEASURED in `dismissNotif` (App.tsx): a DERIVED row's
                 id went into `dismissedReminders` and the builder stopped
                 rebuilding it — gone. A PERSISTED row had no such plane, so
                 dismissing it MARKED IT READ on the server and it stayed,
                 dimmed, on every device.

                 ── 128 §2 — THE SPLIT IS GONE, BECAUSE THE ASYMMETRY IS ─────
                 [MEASURED at 9f5c3da3] `notifications.js` carried four verbs
                 and none of them removed anything, which is why the persisted
                 arm existed at all — it described a fallback, not a design.
                 §2 gave the persisted plane a real dismissal
                 (`PATCH /notifications/:id/dismiss`, `GET /me` stops serving
                 the row), so BOTH PLANES NOW REMOVE, both survive a reload and
                 both reach every device.

                 So the tooltip stops forking. Two sentences for one act, on
                 one row, is the defect 126 §6 named one level up ("two words
                 for one act, on one screen") — the plane split was justified
                 only for as long as the planes did different things to the
                 reader, and they no longer do. `notif.remove_tip_persisted`
                 keeps its seeded rows and loses its call site, exactly as
                 `notif.dismiss` did in §6 (D28).

                 The remaining sentence still answers the misreading the word
                 invites — "Remove" must not read as "delete my task". */
              title={t("notif.remove_tip", {
                fallback:
                  "Removes this row from the list — the task itself is untouched",
              })}
              /* 209 §26 [Laurent] — « "dismiss" should be a button not just
                 clickable text — same style as the "+add" buttons, but in
                 amber ». AddButton's shape (SharedUI.tsx: `btn`, its padding
                 and size), amber: `.btn-amber` (App.css), --am on --ams. */
              className="btn btn-amber"
              style={{ padding: "2px 7px", fontSize: FS.s10, marginLeft: "auto" }}
              onClick={(e6) => {
                e6.stopPropagation();
                /* Brief 42 follow-on — one call,
                   both planes. This used to push
                   n.dismissId straight into
                   dismissedReminders, which a
                   persisted row is never filtered
                   against: "Ignorer" on an alert
                   did nothing at all. */
                dismissNotif(n);
              }}
            >
              {t("notif.remove", { fallback: "Remove" })}
            </button>
          )}
          {/* ── 126 §2 — THE RULE STOPS BEING INVISIBLE ────────────────────
              RULED: nothing becomes dismissible. `utils.ts:120` already
              states why — a Dismiss here would be "a second mechanism for a
              fact the row already states", which brief 35 §5c forbids — and
              Laurent's ruling of 2026-08-07 sits beside the overdue row: "an
              overdue marker is a standing state… it announces that something
              IS TRUE." What changes is that the reader is told.

              QUIET TEXT, NOT A TOOLTIP, and the choice is enumerated because
              §2 asks for it:
                (a) the defect is that the rule is INVISIBLE, and a tooltip is
                    invisible until somebody goes looking — the same defect
                    wearing a hat;
                (b) the Daily Overview opens unbidden on a morning and is read
                    at a glance; a hover is not read at a glance;
                (c) there is no hover on a touch surface at all;
                (d) it costs the row nothing: it occupies the SLOT THE DISMISS
                    BUTTON WOULD, at the same size and colour, so a row with a
                    reason and a row with a button line up rather than looking
                    like two kinds of row.
              The full sentence rides in `title` as well, because the slot is
              narrow and this ellipsises rather than wrapping the line. */}
          {!n.dismissId && n._clears && (
            <span
              title={clearsText(t, n._clears)}
              style={{
                fontSize: FS.s8,
                color: "var(--tm)",
                fontStyle: "italic",
                lineHeight: "14px",
                marginLeft: "auto",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {clearsText(t, n._clears)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
